import { Color, type Object3D, type Material } from 'three';

/**
 * キューの「きらめき」演出（自前・GPL 非依存）。
 *
 * オートエイム（自動照準）の自動補正が走った瞬間に呼ばれ、キューメッシュの emissive を
 * 一瞬光らせてフェードさせる。GPL の cue.ts / cuemesh.ts は改変せず、外から渡された
 * Object3D 配下のマテリアル emissive のみを時間変調する（CLAUDE.md R-2 封じ込め設計）。
 *
 * - trigger(): きらめきを開始（多重呼び出しは先頭から再生し直す）。
 * - update(dt): 毎フレーム dt [s] を渡してフェードを進める（描画ループから駆動）。
 *
 * ⚠ 2026-06-12: 「ちらつき」と判別不能で視覚的にノイズになるため**当面無効化**。
 *   trigger() / update() / setGlowColor() は呼び出し可能だが**いずれも no-op**。
 *   将来の演出見直し時に GLIMMER_DISABLED フラグを false にすれば復活する。
 */
const GLIMMER_DISABLED = true;
export interface CueGlimmer {
  readonly trigger: () => void;
  readonly update: (dt: number) => void;
  /** きらめき色を動的に変更する。未設定/null で既定（暖色）。
   *  カーボンキュー等で暖色が浮く場合に呼び出し側から寒色を指定する用途。 */
  readonly setGlowColor: (cssHex: string | null) => void;
}

/** emissive を持つマテリアル（MeshPhong/MeshStandard 等）の最小形。 */
interface EmissiveMaterial extends Material {
  emissive: Color;
  emissiveIntensity?: number;
}

const hasEmissive = (m: Material | undefined): m is EmissiveMaterial =>
  !!m && (m as { emissive?: unknown }).emissive instanceof Color;

export const createCueGlimmer = (root: Object3D): CueGlimmer => {
  // 無効化フラグ ON: 走査もフェード処理も一切実行せず、no-op API を返して即座に終了する。
  // ロジック（下記の trigger/update/setGlowColor 実装）は将来の復活に備えて残置する。
  if (GLIMMER_DISABLED) {
    return {
      trigger: () => {
        /* no-op: GLIMMER_DISABLED=true */
      },
      update: () => {
        /* no-op: GLIMMER_DISABLED=true */
      },
      setGlowColor: () => {
        /* no-op: GLIMMER_DISABLED=true */
      },
    };
  }

  const DEFAULT_GLOW = new Color(0xfff1c0); // 暖色の光彩（既定・メイプル木と相性が良い）
  const glow = new Color().copy(DEFAULT_GLOW); // 動的に切替可能な実効きらめき色
  const DURATION = 0.35; // きらめきの持続 [s]（短くして気配だけにする）
  // ピーク強度: 1.0 = glow 色に完全到達、0.15 ≒ ベース色 85% を維持して上に薄く乗る程度。
  // 「よく見ないと気付かない程度」を狙って大きく下げる（過去 1.5→0.5→0.15）。
  const PEAK = 0.15;

  // 対象マテリアルと、その元の emissive 色を退避（フェード後に厳密復元するため）。
  const targets: { mat: EmissiveMaterial; base: Color }[] = [];
  try {
    root.traverse((o) => {
      const m = (o as { material?: Material | Material[] }).material;
      const list = Array.isArray(m) ? m : m ? [m] : [];
      for (const mm of list) {
        if (hasEmissive(mm)) targets.push({ mat: mm, base: mm.emissive.clone() });
      }
    });
  } catch (err) {
    // マテリアル走査に失敗してもアプリは継続（きらめきが出ないだけ）。
    console.error('cue-glimmer: マテリアル走査に失敗:', err);
  }

  let t = 0; // 残り時間 [s]
  const tmp = new Color();

  const trigger = (): void => {
    t = DURATION;
  };

  const update = (dt: number): void => {
    if (t <= 0 || targets.length === 0) return;
    t = Math.max(0, t - dt);
    const k = t / DURATION; // 1 → 0
    const glowIntensity = Math.min(1, k * k * PEAK); // 二乗フェード（末尾を絞る）
    for (const { mat, base } of targets) {
      if (t <= 0) {
        mat.emissive.copy(base); // 厳密復元
      } else {
        mat.emissive.copy(tmp.copy(base).lerp(glow, glowIntensity));
      }
    }
  };

  const setGlowColor = (cssHex: string | null): void => {
    if (cssHex) {
      try {
        glow.set(cssHex);
        return;
      } catch (err) {
        console.error('cue-glimmer: 色のパースに失敗:', err);
      }
    }
    // null/失敗時は既定（暖色）へ戻す。
    glow.copy(DEFAULT_GLOW);
  };

  return { trigger, update, setGlowColor };
};
